import React, { useEffect, useRef, useCallback, useState } from 'react'
import { styled } from 'styled-components'
import {
	useCurrentCharacter,
	useCurrentVocalTrack,
	useResolvers,
	useGetActionDuration,
	useMissionStateSelector,
	isPaused,
} from '../../../context'
import { useTransition, animated, useSpring, config } from '@react-spring/web'
const SVG_HEIGHT = 229.29
// Displays the current vocal track and captions as part of frame
export default function VocalTrackAnimations(props: {}): React$Node {
	const vocalTrack = useCurrentVocalTrack()
	const getActionDuration = useGetActionDuration()
	const character = useCurrentCharacter()
	const [vocalTrackData, setVocalTrackData] = useState()

	useEffect(() => {
		if (vocalTrack && character) {
			setVocalTrackData(state => {
				if (state?._actionId === vocalTrack._actionId) {
					return state
				}
				return {
					_actionId: vocalTrack._actionId,
					characterIconUrl: character.iconUrl,
					characterName: character.name,
					closedCaption: vocalTrack.caption,
					audioLength: ((getActionDuration(vocalTrack._actionId): any): number),
				}
			})
		}
	}, [vocalTrack, character, getActionDuration])

	const transition = useTransition(
		vocalTrack && vocalTrack?._actionId === vocalTrackData?._actionId,
		{
			key: vocalTrack ? 'active' : 'inactive',
			from: { y: SVG_HEIGHT },
			enter: { y: 0 },
			leave: {
				y: SVG_HEIGHT,
				delay: 1000,
				config: config.molasses,
			},
		}
	)

	const { imageSourceResolver } = useResolvers()

	return transition(
		({ y }, shouldDisplay) =>
			shouldDisplay && (
				<Svg {...props} viewBox="0 0 966.93 229.29" y={y}>
					<g>
						<path
							className="caption-1"
							d="M921.33,179.29H45.6L0,133.7V34.28A34.28,34.28,0,0,1,34.28,0H932.65a34.28,34.28,0,0,1,34.28,34.28V133.7Z"
						/>
						<rect
							className="caption-2"
							x="13.55"
							y="12.94"
							width="938.13"
							height="121.04"
							rx="18.78"
						/>
						<path
							className="caption-1"
							d="M31.15,141.65H344.06a17.54,17.54,0,0,1,17.54,17.54v14.5H63.2Z"
						/>

						{vocalTrackData ? (
							<g>
								<image
									x="23.55"
									y="21.04"
									href={imageSourceResolver(vocalTrackData.characterIconUrl)}
									preserveAspectRatio
									height="110"
								/>
								<foreignObject
									x="153.55"
									y="36.94"
									height="73.04"
									width="795.78">
									<AutoScrollContainer
										text={vocalTrackData.closedCaption}
										length={vocalTrackData.audioLength}
									/>
								</foreignObject>

								<CharacterName fontSize="32" x="50%" y="156">
									{vocalTrackData.characterName}
								</CharacterName>
							</g>
						) : null}
					</g>
				</Svg>
			)
	)
}

const CharacterName = styled.text.attrs({ className: 'font-game-headline' })`
	dominant-baseline: middle;
	text-anchor: middle;
	fill: white;
`

const Svg = styled(animated.svg)`
	.caption-1 {
		fill: #364984;
	}

	.caption-2 {
		fill: #0f193c;
	}
`

/**
 * Automatically scrolls text to bottom of container if need be for captions
 * @param {string} props.text The text that will be scrolled in the container
 * @param {string} props.length The length of the vocal track to determine how fast to scroll.
 * @returns {React$Node}
 */
function AutoScrollContainer({
	text,
	length,
}: {
	text: string,
	length: number,
}): React$Node {
	const isMissionPaused = useMissionStateSelector(isPaused)

	const containerRef = useRef()
	const captionRef = useRef()
	const [{ y }, scroll] = useSpring(() => ({ y: 0 }))

	useEffect(() => {
		if (isMissionPaused) {
			scroll.pause()
		} else {
			scroll.resume()
		}
	}, [isMissionPaused, scroll])

	const scrollToBottom = useCallback(() => {
		const caption = captionRef.current
		const container = containerRef.current
		if (!caption || !container || !text) {
			return
		}
		let y = caption.offsetHeight - container.offsetHeight
		if (y < 0) {
			return
		}
		scroll.start({
			reset: true,
			from: { y: 0 },
			to: { y },
			delay: 1500,
			config: {
				duration: length - 1500,
			},
		})
	}, [scroll, length, text])

	useEffect(() => {
		scrollToBottom()
		return () => {
			scroll.stop()
			scroll.start({ to: 0, immediate: true })
		}
	}, [text, scrollToBottom, scroll])

	return (
		<CaptionWrapper ref={containerRef}>
			<Caption
				style={{ transform: y.to(o => `translateY(-${o}px)`) }}
				ref={captionRef}>
				{text}
			</Caption>
		</CaptionWrapper>
	)
}

const CaptionWrapper = styled.div`
	height: 100%;
	padding: 0px 24px;
	overflow: hidden;
`

const Caption = styled(animated.div)`
	font-size: 32px;
	height: fit-content;
`
